import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Fuel, Loader2, Wallet } from "lucide-react";
import { topUpGas } from "../../../lib/investAdminApi";
import {
  CopyHex, EmptyState, ErrorBanner, FilterChips, Investor, LOW_GAS_ETH, RowsSkeleton, SearchInput,
  SectionHeader, StatusBadge, fmtDateTime, fmtEth, fmtNum, fmtUsd,
} from "./bcShared";

const COLS = "md:grid-cols-[1.6fr,1.2fr,1fr,1fr,1fr,auto]";

export default function BlockchainWallets({ wallets, orders, loading, error, onRetry, onChanged }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [toppingUp, setToppingUp] = useState(null);

  const invested = useMemo(() => {
    const m = {};
    orders.filter((o) => o.status === "COMPLETED").forEach((o) => {
      m[o.userId] = m[o.userId] || { tokens: 0, cents: 0 };
      m[o.userId].tokens += o.quantity;
      m[o.userId].cents += o.fees?.totalCents || 0;
    });
    return m;
  }, [orders]);

  const isLow = (w) => w.ethBalance != null && Number(w.ethBalance) < LOW_GAS_ETH;
  const lowCount = wallets.filter(isLow).length;

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return wallets.filter((w) => {
      if (filter === "low" && !isLow(w)) return false;
      if (!needle) return true;
      return [w.walletAddress, w.investor?.name, w.investor?.email, w.username].some((v) => v?.toLowerCase().includes(needle));
    });
  }, [wallets, filter, q]);

  const topUp = async (w) => {
    setToppingUp(w.id);
    try {
      const r = await topUpGas(w.walletAddress);
      if (r.toppedUp) toast.success("Gas topped up");
      else toast.info("Wallet already has enough gas — no top-up needed");
      onChanged();
    } catch (e) {
      toast.error(e.message || "Top-up failed");
    } finally {
      setToppingUp(null);
    }
  };

  return (
    <div className="zc-card overflow-hidden zc-stack-table" data-testid="bc-wallets">
      <SectionHeader title="Custodial investor wallets" count={wallets.length}>
        <div className="flex flex-wrap items-center gap-3">
          <FilterChips
            value={filter}
            onChange={setFilter}
            options={[
              { id: "all", label: "All", count: wallets.length },
              { id: "low", label: "Low gas", count: lowCount },
            ]}
          />
          <SearchInput value={q} onChange={setQ} placeholder="Search investor or address…" />
        </div>
        <p className="text-2xs text-[var(--zc-text-3)]">
          Each investor gets a platform-held wallet. Gas (ETH) is only needed when they withdraw; wallets under {LOW_GAS_ETH} ETH are topped up automatically at that point.
        </p>
      </SectionHeader>

      {error && <ErrorBanner message={error} onRetry={onRetry} />}

      <div className={`hidden md:grid ${COLS} gap-4 zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold`}>
        <div>Investor</div><div>Wallet</div><div>Invested</div><div>Gas balance</div><div>Created</div><div className="w-28" />
      </div>

      {loading && wallets.length === 0 && <RowsSkeleton />}
      {!loading && !error && rows.length === 0 && (
        <EmptyState icon={Wallet} title={wallets.length ? "No wallets match" : "No investor wallets yet"} hint={wallets.length ? "Try a different filter or search." : "A wallet is created the first time a user opens the Invest page."} />
      )}

      {rows.map((w) => {
        const inv = invested[w.id];
        return (
          <div key={w.id} className={`zc-stack-row md:grid ${COLS} md:items-center gap-4 px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0 text-sm`}>
            <div data-label="Investor" className="min-w-0">
              {w.investor?.name || w.investor?.email
                ? <Investor investor={w.investor} />
                : <span className="text-xs font-mono text-[var(--zc-text-3)]">{w.username}</span>}
            </div>
            <div data-label="Wallet"><CopyHex value={w.walletAddress} label="Wallet address" /></div>
            <div data-label="Invested" className="tabular-nums">
              {inv ? <><div className="font-semibold">{fmtUsd(inv.cents)}</div><div className="text-2xs text-[var(--zc-text-3)]">{fmtNum(inv.tokens)} tokens</div></> : <span className="text-[var(--zc-text-3)]">—</span>}
            </div>
            <div data-label="Gas balance" className="flex items-center gap-2 tabular-nums">
              <span>{fmtEth(w.ethBalance)}</span>
              {isLow(w) && <StatusBadge tone="warn">Low</StatusBadge>}
            </div>
            <div data-label="Created" className="text-xs text-[var(--zc-text-2)]">{fmtDateTime(w.createdAt)}</div>
            <div data-label="Actions" className="zc-stack-actions md:w-28 md:flex md:justify-end">
              <button
                type="button"
                onClick={() => topUp(w)}
                disabled={toppingUp === w.id}
                className="zc-btn-ghost px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {toppingUp === w.id ? <Loader2 size={12} className="animate-spin" /> : <Fuel size={12} />}
                Top up gas
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
